"use client";

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { formatDate } from '@/lib/utils/helpers';

interface JobData {
  id: string;
  title: string;
  responsibilities: string;
  hook: string;
  must_have: string;
  nice_to_have: string | null;
  what_we_offer: string;
  salary_range: string | null;
  employment_details: {
    employment_type: string;
    location: string;
    location_type: 'På plats' | 'Hybrid' | 'Distans' | null;
    start_date: string | null;
  };
  about_company: string | null;
  application_process: string | null;
  location: string;
  job_type: string; // legacy/fallback
  location_type?: 'På plats' | 'Hybrid' | 'Distans' | null; // legacy/fallback
  salary_range_numeric?: {
    min: number;
    max: number;
    currency: string;
    is_visible: boolean;
  };
  company: {
    id: string;
    name: string;
    logo_url?: string;
    location?: string;
    website?: string;
    description?: string;
  };
  company_description?: string;
  created_at: string;
  is_published?: boolean;
  isExternal?: boolean;
  externalUrl?: string;
  screening_questions?: Array<{
    id: string;
    question: string;
    type: 'text' | 'single_choice' | 'multi_choice' | 'yes_no' | 'rating';
    options?: string[];
    required: boolean;
    weight?: number;
    correct_answer?: string;
    correct_answers?: string[];
    min_score?: number;
  }>;
}

interface ApplicationFormData {
  fullName: string;
  email: string;
  phone?: string;
  linkedinUrl?: string;
  coverLetter?: string;
}

export default function JobPage({ params }: { params: { id: string } }) {
  const jobId = params.id;
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<JobData | null>(null);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, any>>({});
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ApplicationFormData>();
  const companyWebsiteUrl = job?.company?.website
    ? job.company.website.startsWith('http')
      ? job.company.website
      : `https://${job.company.website}`
    : null;

  const handleAnswerChange = (questionId: string, value: any) => {
    setQuestionAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const toggleMultiChoice = (questionId: string, option: string) => {
    setQuestionAnswers(prev => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const next = current.includes(option)
        ? current.filter((item: string) => item !== option)
        : [...current, option];
      return { ...prev, [questionId]: next };
    });
  };

  const handleResumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setResumeError(null);
    setResumeFile(null);

    if (!file) return;

    const allowedTypes = new Set([
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]);

    if (!allowedTypes.has(file.type)) {
      setResumeError('Endast PDF eller Word-filer (DOC, DOCX) tillåts.');
      return;
    }

    const maxSizeMb = 10;
    if (file.size > maxSizeMb * 1024 * 1024) {
      setResumeError('Filen är för stor. Max 10 MB.');
      return;
    }

    setResumeFile(file);
  };
  
  const fetchJob = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch from API
      const response = await fetch(`/api/jobs/${jobId}`, { cache: 'no-store' });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Kunde inte ladda jobbdata');
      }
      
      const jobData = await response.json();
      
      // Handle different response formats
      let formattedJob: JobData;
      
      // If it's a company-created job (has title, description, etc.)
      if (jobData.title && (jobData.hook !== undefined || jobData.description !== undefined)) {
        const isPublished =
          jobData.is_published === true || jobData.is_published === 'true';

        formattedJob = {
          id: jobData.id,
          title: jobData.title,
          responsibilities: jobData.responsibilities || '',
          hook: jobData.hook ?? jobData.description ?? '',
          must_have: jobData.must_have ?? jobData.requirements ?? '',
          nice_to_have: jobData.nice_to_have ?? null,
          what_we_offer: jobData.what_we_offer ?? '',
          salary_range: jobData.salary_range ?? null,
          employment_details: jobData.employment_details ?? {
            employment_type: jobData.job_type || 'full-time',
            location: jobData.location || '',
            location_type: jobData.location_type ?? null,
            start_date: null,
          },
          about_company: jobData.about_company ?? null,
          application_process: jobData.application_process ?? null,
          location: jobData.location || '',
          job_type: jobData.job_type || 'full-time',
          location_type: jobData.location_type ?? null,
          salary_range_numeric: jobData.salary_range_numeric,
          company: jobData.company || {
            id: 'unknown',
            name: 'Okänt företag'
          },
          created_at: jobData.created_at || new Date().toISOString(),
          screening_questions: jobData.screening_questions || [],
          is_published: isPublished
        };
      } else {
        // If it's a Jobtech or external job (has headline, employer, etc.)
        const isExternal = jobData.isExternal || jobId.startsWith('external_');
        formattedJob = {
          id: jobData.id || jobId,
          title: jobData.headline || jobData.title || 'Okänd titel',
          hook: typeof jobData.description === 'string'
            ? jobData.description
            : jobData.description?.text || '',
          responsibilities: '',
          must_have: '',
          nice_to_have: null,
          what_we_offer: '',
          salary_range: null,
          employment_details: {
            employment_type: 'full-time',
            location: jobData.workplace_address?.municipality 
              || jobData.workplace_address?.city 
              || jobData.workplace_address 
              || '',
            location_type: null,
            start_date: null,
          },
          about_company: null,
          application_process: null,
          location: jobData.workplace_address?.municipality 
            || jobData.workplace_address?.city 
            || jobData.workplace_address 
            || '',
          job_type: 'full-time',
          location_type: null,
          salary_range_numeric: undefined,
          company: {
            id: isExternal ? 'external' : 'external',
            name: jobData.employer?.name || 'Okänt företag',
            logo_url: jobData.logo_url,
            website: jobData.application_details?.url || (isExternal ? (jobData.url || jobData.application_details?.url) : undefined),
            location: jobData.workplace_address?.municipality
          },
          created_at: jobData.publication_date || jobData.created_at || new Date().toISOString(),
          screening_questions: [],
          isExternal: isExternal,
          externalUrl: isExternal ? (jobData.url || jobData.application_details?.url) : undefined
        };
      }
      
      setJob(formattedJob);
    } catch (err: any) {
      console.error('Error loading job data:', err);
      setError(err.message || 'Kunde inte ladda jobbdata');
    } finally {
      setIsLoading(false);
    }
  };

  // Load job data
  useEffect(() => {
    fetchJob();
  }, [jobId]);
  
  // Handle application submission
  const onSubmit = async (data: ApplicationFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);
      setResumeError(null);

      const screeningQuestions = job?.screening_questions || [];
      if (screeningQuestions.length > 0) {
        for (const question of screeningQuestions) {
          if (!question?.required) continue;
          const answer = questionAnswers[question.id];
          if (question.type === 'multi_choice') {
            if (!Array.isArray(answer) || answer.length === 0) {
              setError('Vänligen besvara alla obligatoriska frågor.');
              return;
            }
          } else if (answer === undefined || answer === null || answer === '') {
            setError('Vänligen besvara alla obligatoriska frågor.');
            return;
          }
        }
      }
      
      let resumePath: string | null = null;
      if (!resumeFile) {
        setResumeError('CV krävs.');
        setIsSubmitting(false);
        return;
      }
      if (resumeFile) {
        const formData = new FormData();
        formData.append('file', resumeFile);
        const uploadResponse = await fetch('/api/applications/resume', {
          method: 'POST',
          body: formData
        });

        if (!uploadResponse.ok) {
          setResumeError('CV-uppladdningen misslyckades. Försök igen.');
          return;
        }

        const uploadData = await uploadResponse.json();
        resumePath = uploadData.path;
      }

      const formattedAnswers: Record<string, any> = {};
      (job?.screening_questions || []).forEach((question) => {
        formattedAnswers[question.id] = {
          question: question.question,
          type: question.type,
          required: question.required,
          weight: question.weight || 1,
          options: question.options || [],
          answer: questionAnswers[question.id] ?? null,
          correct_answer: question.correct_answer,
          correct_answers: question.correct_answers,
          min_score: question.min_score
        };
      });

      const response = await fetch('/api/applications/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jobId,
          resume: resumePath,
          cover_letter: data.coverLetter || '',
          answers: formattedAnswers,
          source: 'website',
          fullName: data.fullName,
          email: data.email,
          phone: data.phone || null,
          linkedinUrl: data.linkedinUrl || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Kunde inte skicka ansökan');
      }

      // Show success message
      setSubmitSuccess(true);
      reset();
      setResumeFile(null);
      setQuestionAnswers({});
      
    } catch (err: any) {
      console.error('Error submitting application:', err);
      setError(err.message || 'Kunde inte skicka ansökan');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }
  
  // Error state
  if (error && !submitSuccess) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-2 text-sm text-red-600 dark:text-red-400 underline"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }
  
  // Missing job state
  if (!job) {
    return (
      <div className="p-8 text-center min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Jobb hittades inte</h1>
        <p className="text-slate-700 dark:text-slate-300 mb-6">Den här annonsen kan ha tagits bort eller är inte längre tillgänglig.</p>
        <a href="/jobs" className="text-primary-600 dark:text-primary-400 hover:underline">
          Se andra jobbannonser
        </a>
      </div>
    );
  }
  
  // Success state
  if (submitSuccess) {
    return (
      <div className="p-8 max-w-3xl mx-auto min-h-screen flex flex-col items-center justify-center">
        <div className="w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 border border-slate-200 dark:border-slate-700 text-center">
          <div className="mb-6 text-green-500 dark:text-green-400">
            <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Ansökan skickades!</h1>
          <p className="text-slate-700 dark:text-slate-300 mb-6">
            Tack för att du sökte tjänsten <strong>{job.title}</strong> hos <strong>{job.company.name}</strong>.
            Vi återkommer så snart vi har granskat din ansökan.
          </p>
          <div className="flex justify-center">
            <a 
              href="https://karrelo.com"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              Utforska Karrelo
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2">
          {/* Company header */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6 border border-slate-200 dark:border-slate-700 mb-8">
            <div className="flex items-center">
              {job.company.logo_url ? (
                <img 
                  src={job.company.logo_url} 
                  alt={`${job.company.name} logo`}
                  className="h-16 w-16 object-contain rounded-md mr-4 bg-white"
                />
              ) : (
                <div className="h-16 w-16 flex items-center justify-center rounded-md bg-slate-100 dark:bg-slate-700 mr-4 text-slate-400 dark:text-slate-500 font-semibold text-xl">
                  {job.company.name.charAt(0)}
                </div>
              )}
              
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{job.title}</h1>
                  {job.isExternal && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                      Extern
                    </span>
                  )}
                </div>
                <p className="text-lg text-slate-700 dark:text-slate-300 mt-1">{job.company.name}</p>
              </div>
            </div>
            
            {job.isExternal && job.externalUrl && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">
                  Detta jobb importerades från en extern källa.
                </p>
                <a
                  href={job.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium underline inline-flex items-center gap-1"
                >
                  Visa originalannons <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}
            
            <div className="mt-6 flex flex-wrap gap-4">
              <div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Plats</div>
                <div className="text-slate-900 dark:text-white">{job.location}</div>
              </div>
              
              <div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Anställningstyp</div>
                <div className="text-slate-900 dark:text-white">
                  {job.employment_details?.employment_type || job.job_type.replace('-', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </div>
              </div>

              {job.employment_details?.location_type ? (
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Arbetsplats</div>
                  <div className="text-slate-900 dark:text-white">{job.employment_details.location_type}</div>
                </div>
              ) : null}
              
              {job.salary_range ? (
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Lön</div>
                  <div className="text-slate-900 dark:text-white font-semibold">{job.salary_range}</div>
                </div>
              ) : (job.salary_range_numeric && job.salary_range_numeric.is_visible ? (
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Löneintervall</div>
                  <div className="text-slate-900 dark:text-white">
                    {job.salary_range_numeric.currency === 'USD' ? '$' : job.salary_range_numeric.currency}
                    {job.salary_range_numeric.min.toLocaleString()} - {job.salary_range_numeric.max.toLocaleString()}
                  </div>
                </div>
              ) : null)}
              
              <div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Publicerad</div>
                <div className="text-slate-900 dark:text-white">{formatDate(job.created_at)}</div>
              </div>
            </div>
          </div>
          
          {/* Job description */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 border border-slate-200 dark:border-slate-700">
            {job.hook ? (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Om jobbet</h2>
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line text-base">{job.hook}</p>
              </div>
            ) : null}
            {job.what_we_offer ? (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Detta erbjuder vi</h2>
                <div className="text-slate-700 dark:text-slate-300 whitespace-pre-line">{job.what_we_offer}</div>
              </div>
            ) : null}

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Huvudsakliga arbetsuppgifter</h2>
              <div className="text-slate-700 dark:text-slate-300 whitespace-pre-line">{job.responsibilities}</div>
            </div>
            
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Vi söker dig som har</h2>
              <div className="text-slate-700 dark:text-slate-300 whitespace-pre-line">{job.must_have}</div>
            </div>

            {job.nice_to_have ? (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Vi ser gärna att du har</h2>
                <div className="text-slate-700 dark:text-slate-300 whitespace-pre-line">{job.nice_to_have}</div>
              </div>
            ) : null}
            
            {job.application_process ? (
              <div className="mb-2">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Ansökan</h2>
                <div className="text-slate-700 dark:text-slate-300 whitespace-pre-line">
                  {job.application_process}
                </div>
              </div>
            ) : null}

            {job.about_company ? (
              <div className="mt-8">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">Om företaget</h2>
                <div className="text-slate-700 dark:text-slate-300 whitespace-pre-line">
                  {job.about_company}
                  {companyWebsiteUrl ? (
                    <p className="mt-3">
                      <a href={companyWebsiteUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                        Besök {job.company.website?.replace(/^https?:\/\//, '') || 'webbplatsen'}
                      </a>
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Apply card */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6 border border-slate-200 dark:border-slate-700 mb-6 sticky top-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Ansök till tjänsten</h2>
            
            {!showApplyForm ? (
              <div className="space-y-4">
                <button
                  onClick={() => setShowApplyForm(true)}
                  className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Ansök nu
                </button>
                
                <div className="text-sm text-slate-500 dark:text-slate-400 text-center">
                  Ansök direkt utan konto.
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Fullständigt namn*
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    {...register('fullName', { required: 'Fullständigt namn krävs' })}
                    className={`w-full px-3 py-2 border ${errors.fullName ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white`}
                  />
                  {errors.fullName && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fullName.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    E-post*
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register('email', { 
                      required: 'E-post krävs',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Ogiltig e-postadress'
                      }
                    })}
                    className={`w-full px-3 py-2 border ${errors.email ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Telefonnummer*
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    {...register('phone', { required: 'Telefonnummer krävs' })}
                    className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-300 dark:border-red-600' : 'border-slate-300 dark:border-slate-600'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white`}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phone.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="linkedinUrl" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    LinkedIn-profil
                  </label>
                  <input
                    id="linkedinUrl"
                    type="url"
                    {...register('linkedinUrl')}
                    placeholder="https://linkedin.com/in/dittnamn"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white"
                  />
                </div>
                
                <div>
                  <label htmlFor="coverLetter" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Personligt brev
                  </label>
                  <textarea
                    id="coverLetter"
                    rows={4}
                    {...register('coverLetter')}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white"
                    placeholder="Varför är du intresserad av den här tjänsten? (Valfritt)"
                  />
                </div>

                {(job?.screening_questions || []).length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      Frågor från arbetsgivaren
                    </h3>
                    {job?.screening_questions?.map((question) => (
                      <div key={question.id} className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          {question.question}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </label>

                        {question.type === 'text' && (
                          <textarea
                            rows={3}
                            value={questionAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white"
                          />
                        )}

                        {question.type === 'yes_no' && (
                          <select
                            value={questionAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white"
                          >
                            <option value="">Välj</option>
                            <option value="yes">Ja</option>
                            <option value="no">Nej</option>
                          </select>
                        )}

                        {question.type === 'rating' && (
                          <select
                            value={questionAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-slate-700 dark:text-white"
                          >
                            <option value="">Välj betyg</option>
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <option key={rating} value={rating}>{rating}</option>
                            ))}
                          </select>
                        )}

                        {question.type === 'single_choice' && (
                          <div className="space-y-2">
                            {(question.options || []).map((option) => (
                              <label key={option} className="flex items-center text-sm text-slate-700 dark:text-slate-300">
                                <input
                                  type="radio"
                                  name={`question-${question.id}`}
                                  value={option}
                                  checked={questionAnswers[question.id] === option}
                                  onChange={() => handleAnswerChange(question.id, option)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300"
                                />
                                <span className="ml-2">{option}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        {question.type === 'multi_choice' && (
                          <div className="space-y-2">
                            {(question.options || []).map((option) => (
                              <label key={option} className="flex items-center text-sm text-slate-700 dark:text-slate-300">
                                <input
                                  type="checkbox"
                                  checked={(questionAnswers[question.id] || []).includes(option)}
                                  onChange={() => toggleMultiChoice(question.id, option)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300"
                                />
                                <span className="ml-2">{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label htmlFor="resume" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Ladda upp CV (PDF eller Word)*
                  </label>
                  <input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleResumeChange}
                    className="w-full text-sm text-slate-600 dark:text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 dark:file:bg-slate-700 dark:file:text-slate-200 dark:hover:file:bg-slate-600"
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Max 10 MB. Tillåtna format: PDF, DOC, DOCX.
                  </p>
                  {resumeError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{resumeError}</p>
                  )}
                </div>
                
                
                {(error || resumeError) && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                    {error || resumeError}
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowApplyForm(false)}
                    className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    Avbryt
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Skickar...' : 'Skicka ansökan'}
                  </button>
                </div>
              </form>
            )}
          </div>
          
          {/* Company card */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6 border border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{job.company.name}</h2>
            
            <div className="mb-4">
              {job.company.logo_url && (
                <img 
                  src={job.company.logo_url} 
                  alt={`${job.company.name} logo`}
                  className="h-16 w-16 object-contain rounded-md mb-4"
                />
              )}
              
              {job.company.location && (
                <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                  <span className="font-medium">Plats:</span> {job.company.location}
                </div>
              )}
              
              {companyWebsiteUrl && (
                <div className="text-sm mb-4">
                  <a 
                    href={companyWebsiteUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Besök webbplats
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
